import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { IoMenu } from "react-icons/io5";
import {
  ArrowRight,
  ChevronDown,
  ChevronUp,
  Globe,
  Target,
  Trophy,
  Users,
  Zap,
} from "lucide-react";
import { cn } from "../../hooks/cn";
import { useLanguages } from "src/hooks/useLanguages";
import i18next from "i18next";
import { IoIosClose, IoIosFootball } from "react-icons/io";
import { FaFootballBall, FaHockeyPuck } from "react-icons/fa";
import { FaBasketball } from "react-icons/fa6";

// Sports items data

interface Props {
  isRegister?: boolean;
}

const Navbar: React.FC<Props> = ({ isRegister }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showChangeLanguage, setShowChangeLanguage] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [showSportsDropdown, setShowSportsDropdown] = useState(false);
  const { options, languageChosen } = useLanguages();
  const navigationItems = [
    { name: t("header.product"), path: "/product" },
    { name: t("header.app"), path: "/app" },
    { name: t("header.pricing"), path: "/pricing" },
    /*   { name: "Company", path: "/company" },
     */
  ];

  const sportsItems = [
    {
      name: t("home.navbar.sport_items.football.title"),
      path: "/football",
      icon: <IoIosFootball className="w-5 h-5" />,
      desc: t("home.navbar.sport_items.football.desc"),
      color: "from-purple-500 to-indigo-600",
      disabled: false,
    },
    {
      name: t("home.navbar.sport_items.basketball.title"),
      path: "/",
      icon: <FaBasketball className="w-5 h-5" />,
      desc: t("home.navbar.sport_items.basketball.desc"),
      color: "from-red-500 to-orange-600",
      disabled: true,
    },
    {
      name: t("home.navbar.sport_items.rugby.title"),
      path: "/",
      icon: <FaFootballBall className="w-5 h-5" />,
      desc: t("home.navbar.sport_items.rugby.desc"),
      color: "from-green-500 to-teal-600",
      disabled: true,
    },
    {
      name: t("home.navbar.sport_items.hockey.title"),
      path: "/",
      icon: <FaHockeyPuck className="w-5 h-5" />,
      desc: t("home.navbar.sport_items.hockey.desc"),
      color: "from-blue-500 to-cyan-600",
      disabled: true,
    },
  ];

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div
      className={cn(
        "fixed z-50 w-full transition-all duration-500",
        isRegister && "relative h-28"
      )}
    >
      {/* Main header with animated background */}
      <header
        className={cn(
          "transition-all duration-700 relative ",
          scrolled
            ? "bg-gray-900/95 backdrop-blur-lg shadow-xl h-20"
            : "bg-gradient-to-b from-gray-900/90 via-gray-900/60 to-transparent h-28",
          isRegister && "bg-gray-900/95 backdrop-blur-lg h-28"
        )}
      >
        {/* Animated grid background */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.1)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.1)_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_110%)]" />
        </div>

        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-purple-900/20 via-transparent to-blue-900/20" />

        {/* Main navigation container */}
        <nav
          className={cn(
            "relative mx-auto flex max-w-7xl flex-wrap items-center justify-between px-4 sm:px-6 xl:px-8 transition-all duration-500",
            scrolled ? "py-4" : "py-6"
          )}
        >
          {/* Logo section */}
          <div className="flex items-center">
            <button
              onClick={() => navigate("/")}
              className="group flex items-center gap-3 transition-all duration-300 hover:scale-105"
            >
              {/* Logo with gradient border */}
              <div className="relative">
                <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-purple-500 to-blue-500 blur-md opacity-30 group-hover:opacity-60 transition-opacity duration-500" />
                <div className="relative flex items-center justify-center w-12 h-12 rounded-xl bg-gray-800/80 backdrop-blur-sm border border-gray-700 group-hover:border-purple-500/50 transition-colors duration-300 overflow-hidden">
                  <img
                    src={require("../../assets/wizard-logo.jpg")}
                    alt="Wizard Logo"
                    className="w-8 h-8 object-contain transition-transform duration-500 group-hover:rotate-6"
                  />
                </div>
              </div>

              {/* Logo text with gradient */}
              <span className="text-2xl font-bold bg-gradient-to-r from-white via-purple-200 to-blue-200 bg-clip-text text-transparent tracking-tight">
                Wizard
              </span>
            </button>
          </div>

          {/* Mobile menu button */}
          <div className="flex xl:hidden">
            <button
              onClick={() => setMobileMenuOpen(true)}
              className="p-2 rounded-lg text-gray-300 hover:text-white hover:bg-gray-800 transition-colors duration-300"
            >
              <IoMenu className="w-6 h-6" />
              <span className="sr-only">{t("home.navbar.open_menu")}</span>
            </button>
          </div>

          {/* Desktop navigation */}
          <div className="hidden xl:flex xl:flex-wrap xl:items-center xl:gap-6">
            {/* Main navigation items */}
            <div className="flex items-center gap-1">
              {navigationItems.map((item) => (
                <button
                  key={item.name}
                  onClick={() => navigate(item.path)}
                  className="px-4 py-2.5 text-sm font-medium text-gray-300 hover:text-white rounded-lg transition-colors duration-300 hover:bg-gray-800/50"
                >
                  {item.name}
                </button>
              ))}

              {/* Sports dropdown */}
              <div className="relative">
                <button
                  onClick={() => setShowSportsDropdown(!showSportsDropdown)}
                  className="flex items-center gap-1 px-4 py-2.5 text-sm font-medium text-gray-300 hover:text-white rounded-lg transition-colors duration-300 hover:bg-gray-800/50"
                >
                  {t("home.navbar.sports")}
                  <ChevronDown
                    className={`w-4 h-4 transition-transform duration-300 ${showSportsDropdown ? "rotate-180" : ""
                      }`}
                  />
                </button>

                {/* Dropdown panel */}
                {showSportsDropdown && (
                  <div className="absolute right-0 mt-2 w-64 bg-gray-900/95 backdrop-blur-lg rounded-xl shadow-2xl border border-gray-800 overflow-hidden z-50">
                    <div className="p-2">
                      {sportsItems.map((item) => (
                        <button
                          key={item.name}
                          onClick={() => {
                            if (!item.disabled) {
                              navigate(item.path);
                              setShowSportsDropdown(false);
                            }
                          }}
                          disabled={item.disabled}
                          className={`w-full flex items-center gap-3 px-4 py-3 text-sm rounded-lg transition-colors duration-300 hover:bg-gradient-to-r ${item.color
                            } hover:bg-opacity-20 ${item.disabled
                              ? "opacity-50 cursor-not-allowed bg-gray-800 text-gray-500 hover:bg-none"
                              : "text-gray-300 hover:text-white"
                            }`}
                        >
                          <div
                            className={`p-2 rounded-lg bg-gradient-to-r ${item.color
                              } ${item.disabled ? "opacity-40" : ""}`}
                          >
                            {item.icon}
                          </div>
                          <div className="text-left">
                            <div className="font-medium">{item.name}</div>
                            <div className="text-xs text-gray-400">
                              {item.desc}
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Language selector */}
            <div className="relative ml-4">
              <button
                onClick={() => setShowChangeLanguage(!showChangeLanguage)}
                className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-300 hover:text-white rounded-lg transition-colors duration-300 hover:bg-gray-800/50"
              >
                <Globe className="w-5 h-5" />
                <span>{languageChosen}</span>
                {showChangeLanguage ? (
                  <ChevronUp className="w-4 h-4" />
                ) : (
                  <ChevronDown className="w-4 h-4" />
                )}
              </button>

              {/* Language dropdown */}
              {showChangeLanguage && (
                <div className="absolute right-0 mt-2 w-40 bg-gray-900/95 backdrop-blur-lg rounded-xl shadow-2xl border border-gray-800 z-50">
                  <div className="py-1">
                    {options?.map((option) => (
                      <button
                        key={option.value}
                        onClick={() => {
                          i18next.changeLanguage(option.value);
                          setShowChangeLanguage(false);
                        }}
                        className={`w-full px-4 py-2 text-sm text-left ${option.value === i18next.language?.slice(0, 2)
                          ? "bg-gray-800 text-white"
                          : "text-gray-300 hover:bg-gray-800/50 hover:text-white"
                          }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Login button */}
            <button
              onClick={() =>
                window.open("https://wizardfootball.com", "_blank")
              }
              className="ml-4 inline-flex shrink-0 items-center gap-2 whitespace-nowrap rounded-lg bg-gradient-to-r from-purple-600 to-blue-600 px-4 py-2.5 text-white font-medium transition-all duration-300 shadow-lg hover:from-purple-700 hover:to-blue-700 hover:shadow-purple-500/20 xl:px-6"
            >
              {t("header.log_in")}
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </nav>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="fixed inset-0 z-40 bg-black/80 backdrop-blur-lg xl:hidden">
            <div className="fixed inset-y-0 right-0 w-full max-w-xs bg-gray-900 shadow-xl">
              <div className="flex items-center justify-between p-5 border-b border-gray-800">
                <button
                  onClick={() => navigate("/")}
                  className="flex items-center gap-3"
                >
                  <div className="w-10 h-10 rounded-lg bg-gray-800 flex items-center justify-center border border-gray-700">
                    <img
                      src={require("../../assets/wizard-logo.jpg")}
                      alt="Wizard Logo"
                      className="w-6 h-6 object-contain"
                    />
                  </div>
                  <span className="text-xl font-bold bg-gradient-to-r from-white via-purple-200 to-blue-200 bg-clip-text text-transparent">
                    Wizard
                  </span>
                </button>
                <button
                  onClick={() => setMobileMenuOpen(false)}
                  className="p-2 rounded-lg text-gray-300 hover:text-white hover:bg-gray-800 transition-colors duration-300"
                >
                  <IoIosClose className="w-6 h-6" />
                  <span className="sr-only">{t("home.navbar.close_menu")}</span>
                </button>
              </div>

              <div className="h-[calc(100vh-80px)] overflow-y-auto bg-gradient-to-br from-slate-900 via-gray-900 to-slate-800 p-6">
                <div className="space-y-6">
                  {/* Mobile navigation items */}
                  {navigationItems.map((item) => (
                    <button
                      key={item.name}
                      onClick={() => {
                        navigate(item.path);
                        setMobileMenuOpen(false);
                      }}
                      className="block w-full px-4 py-3 text-lg font-medium text-gray-300 hover:text-white hover:bg-gray-800/50 rounded-lg transition-colors duration-300 text-left"
                    >
                      {item.name}
                    </button>
                  ))}

                  {/* Mobile sports dropdown */}
                  <div className="space-y-2">
                    <div className="px-4 py-3 text-lg font-medium text-gray-300">
                      {t("home.navbar.sports")}
                    </div>
                    <div className="pl-6 space-y-2">
                      {sportsItems.map((item) => (
                        <button
                          key={item.name}
                          onClick={() => {
                            if (!item.disabled) {
                              navigate(item.path);
                              setMobileMenuOpen(false);
                            }
                          }}
                          disabled={item.disabled}
                          className={`w-full px-4 py-2 text-base font-medium rounded-lg transition-colors duration-300 text-left flex items-center gap-3 ${item.disabled
                            ? "opacity-50 cursor-not-allowed bg-gray-800 text-gray-500 hover:bg-none"
                            : "text-gray-400 hover:text-white hover:bg-gray-800/30"
                            }`}
                        >
                          <div
                            className={`p-1.5 rounded-md bg-gradient-to-r ${item.color
                              } ${item.disabled ? "opacity-40" : ""}`}
                          >
                            {item.icon}
                          </div>
                          {item.name}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Mobile language selector */}
                  <div className="pt-4">
                    <button
                      onClick={() => setShowChangeLanguage(!showChangeLanguage)}
                      className="flex items-center gap-3 w-full px-4 py-3 text-lg font-medium text-gray-300 hover:text-white hover:bg-gray-800/50 rounded-lg transition-colors duration-300 text-left"
                    >
                      <Globe className="w-5 h-5" />
                      <span>Language ({languageChosen})</span>
                      {showChangeLanguage ? (
                        <ChevronUp className="w-5 h-5 ml-auto" />
                      ) : (
                        <ChevronDown className="w-5 h-5 ml-auto" />
                      )}
                    </button>

                    {showChangeLanguage && (
                      <div className="pl-12 pt-2 space-y-1">
                        {options?.map((option) => (
                          <button
                            key={option.value}
                            onClick={() => {
                              i18next.changeLanguage(option.value);
                              setShowChangeLanguage(false);
                            }}
                            className={`block w-full px-4 py-2 text-base font-medium ${option.value === i18next.language?.slice(0, 2)
                              ? "text-white bg-gray-800/50"
                              : "text-gray-400 hover:text-white hover:bg-gray-800/30"
                              } rounded-lg transition-colors duration-300 text-left`}
                          >
                            {option.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Mobile login button */}
                  <div className="pt-6">
                    <button
                      onClick={() =>
                        window.open("https://wizardfootball.com", "_blank")
                      }
                      className="w-full px-6 py-3 rounded-lg bg-gradient-to-r from-purple-600 to-blue-600 text-white font-medium hover:from-purple-700 hover:to-blue-700 transition-all duration-300 shadow-lg hover:shadow-purple-500/20 flex items-center justify-center gap-2"
                    >
                      {t("header.log_in")}
                      <ArrowRight className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </header>
    </div>
  );
};

export default Navbar;
